import './HvacFormGroupTabControl.scss';
import { HvacGroup } from '@belimo-retrofit-portal/logic';
import { FieldPath } from '@form-ts/core';
import { useFormWatch } from '@form-ts/react';
import clsx from 'clsx';
import { pipe } from 'fp-ts/function';
import React, { memo, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'src/modules/common/components/Button';
import { Tooltip } from 'src/modules/common/components/Tooltip';
import { DeleteIcon } from 'src/modules/common/icons/DeleteIcon';
import { FormError } from 'src/modules/form/types/FormError';
import { HvacFormData, HvacFormGroupData } from 'src/modules/hvac/types/HvacFormData';

type Props = {
  readonly group: HvacGroup;
  readonly index: number;
  readonly field: FieldPath<HvacFormData, FormError, HvacFormGroupData>;
  readonly readOnly: boolean;
  readonly onDelete?: (index: number) => void;
};

export const HvacFormGroupTabControl = memo(({ group, index, field, readOnly, onDelete }: Props) => {
  const intl = useIntl();
  const title = useFormWatch(field.form, field.at('title').value.get);

  const [focused, setFocused] = useState<boolean>(false);
  const handleFocus = useCallback(() => setFocused(true), []);
  const handleBlur = useCallback(() => setFocused(false), []);

  const handleRename = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    field.form.change(pipe(
      field.form.currentState,
      field.at('title').value.set(event.target.value),
    ));
  }, [field]);

  const handleDelete = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onDelete?.(index);
  }, [index, onDelete]);

  if (readOnly) {
    return (
      <div className="bp-hvac-form-group-tab-control">
        <div className="bp-hvac-form-group-tab-control__title">
          {title.length === 0
            ? `${intl.formatMessage({ id: GROUP_NAMES_KEYS[group] })} ${index + 1}`
            : title}
        </div>
      </div>
    );
  }

  return (
    <div className="bp-hvac-form-group-tab-control">
      <div className="bp-hvac-form-group-tab-control__title">
        <input
          className={clsx({
            'bp-hvac-form-group-tab-control__input': true,
            'bp-hvac-form-group-tab-control__input--hidden': !focused,
          })}
          value={title}
          placeholder={`${intl.formatMessage({ id: GROUP_NAMES_KEYS[group] })} ${index + 1}`}
          onChange={handleRename}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        <div className={clsx({
          'bp-hvac-form-group-tab-control__name': true,
          'bp-hvac-form-group-tab-control__name--hidden': focused,
        })}
        >
          {title.length === 0
            ? `${intl.formatMessage({ id: GROUP_NAMES_KEYS[group] })} ${index + 1}`
            : title}
        </div>
      </div>
      {onDelete && (
        <div className="bp-hvac-form-group-tab-control__delete">
          <Tooltip
            id={`hvac-form-group-tab-${group}-${index}-${field.path}`}
            value={intl.formatMessage({ id: 'hvac/form/group/actions/delete' })}
          >
            <Button
              type="button"
              kind="ghost"
              size="sm"
              iconOnly={true}
              icon={(
                <div className="bp-hvac-form-group-tab-control__icon">
                  <DeleteIcon/>
                </div>
              )}
              onClick={handleDelete}
            />
          </Tooltip>
        </div>
      )}
    </div>
  );
});

const GROUP_NAMES_KEYS = {
  [HvacGroup.EMISSION]: 'hvac/form/groups/emission',
  [HvacGroup.DISTRIBUTION]: 'hvac/form/groups/distribution',
  [HvacGroup.GENERATION]: 'hvac/form/groups/generation',
  [HvacGroup.AIR_HANDLER]: 'hvac/form/groups/air_handler',
};
